import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useCombined } from './CollegeContext'; 
import { getChatResponse, getChatResponseFree } from './API'; 
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "./ui/card";
import { FaLock , FaRedoAlt } from 'react-icons/fa'; 
import { Button } from "./ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import { Typewriter } from 'react-simple-typewriter';
import './SchoolDetails.css';
import '../global.css';
import ScholarshipTable from './ScholarshipTable';
import PieChartComponent from './PieChartComponent';
import Modal from './Modal';
import LoginModal from './LoginModal';
import { UpgradeTooltip, UpgradeTooltipNoBlur } from './UpgradeTooltip';

const SchoolDetails = () => {
  const [activeTab, setActiveTab] = useState('fetchMeritData');
  const [meritData, setMeritData] = useState('');
  const [otherScholarshipData, setOtherScholarshipData] = useState('');
  const { ipedsId } = useParams();
  const { user, uid, myColleges} = useCombined();
  const [school, setSchool] = useState(null);
  const [messages, setMessages] = useState([]);
  const [botMessage, setBotMessage] = useState('');
  const [userData, setUserData] = useState({});
  const [currentStep, setCurrentStep] = useState('welcome');
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [scholarshipData, setScholarshipData] = useState('');
  const [error, setError] = useState(null); // scholarships/merit table errors
  const [advisorError, setAdvisorError] = useState(null); // AI advisor errors
  const [input, setInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [visibleColleges, setVisibleColleges] = useState([]); // To store the list of visible colleges
  const [userDataLoaded, setUserDataLoaded] = useState(false); // To track if the user data is fully loaded
  const [schoolBlurb, setSchoolBlurb] = useState(null); // Store school blurb for SEO
  const [recommendedSchools, setRecommendedSchools] = useState([]); // Recommended schools from mid-tier list
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const navigate = useNavigate();

  // List of 100 mid-tier school IPEDS IDs from CSV
  const MID_TIER_SCHOOL_IDS = [
    '204501', '203535', '202523', '237057', '120254', '173902', '130590', '229267',
    '195526', '195304', '189088', '160977', '211291', '213385', '212577', '206589',
    '211273', '123165', '121257', '126678', '190099', '212009', '212674', '174844',
    '173647', '195216', '120865', '218070', '221351', '136950', '210401', '168281',
    '214175', '198516', '131159', '163046', '117946', '121150', '122931', '233374',
    '231712', '232423', '127565', '204006', '231174', '191241', '196413', '482705',
    '122436', '209825', '121691', '236595', '236328', '111948', '191968', '192819',
    '217402', '186584', '216597', '164739', '164580', '165334', '207971', '223232',
    '228875', '228246', '181002', '153269', '150163', '144740', '146719', '202480',
    '179159', '131876', '107080', '170532', '146427', '157818', '156408', '217819',
    '131283', '165662', '167996', '236577', '236230', '129525', '130226', '195003',
    '145646', '143084', '170301', '169080', '152600', '445267', '176947', '139861',
    '159391', '237701', '160621', '228723'
  ];

  useEffect(() => {
    const fetchSchoolDetails = async () => {
      setLoading(true); // Set loading state
      try {
        const schoolDocRef = doc(db, 'collegeData', ipedsId);
        const schoolDocSnap = await getDoc(schoolDocRef);
  
        if (schoolDocSnap.exists()) {
          const schoolDetails = schoolDocSnap.data();
          setSchool(schoolDetails);
  
          const welcomeMessage = `Hi, I am an advisor for ${schoolDetails.Name}. Ask me any questions you have!`;
          setMessages([{ role: 'bot', content: welcomeMessage }]);
  
          console.log('School data fetched:', schoolDetails);
          
        
        } 
         else {
          console.error('School data not found');
        }
      } catch (error) {
        console.error('Error fetching school details:', error);
      } finally {
        setLoading(false); // Stop loading state
      }
    };
  
    fetchSchoolDetails();
  }, [ipedsId]); // Add ipedsId as a dependency

  

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user?.uid) {
          const userDocRef = doc(db, 'userData', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            setUserData(data); // Store user data
            setVisibleColleges(data.visibleColleges || []); // Update visible colleges
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setUserDataLoaded(true); // Mark data as loaded
      }
    };
  
    fetchUserData();
  }, [user]);

  useEffect(() => {
    fetchMeritData(false);
  }, [ipedsId, user]);

  // Fetch school blurb for SEO
  useEffect(() => {
    const fetchSchoolBlurb = async () => {
      if (!ipedsId) return;
      
      try {
        const blurbDocRef = doc(db, 'schoolBlurbs', ipedsId);
        const blurbDocSnap = await getDoc(blurbDocRef);
        
        if (blurbDocSnap.exists()) {
          const blurbData = blurbDocSnap.data();
          setSchoolBlurb({
            shortSummary: blurbData.Short_Summary || blurbData.ShortSummary || '',
            fullBlurb: blurbData.Full_Blurb || blurbData.FullBlurb || '',
          });
        }
      } catch (error) {
        console.error('Error fetching school blurb:', error);
      }
    };
    
    fetchSchoolBlurb();
  }, [ipedsId]);

  // Update meta tags and structured data when school data loads
  useEffect(() => {
    if (!school) return;

    // Use blurb if available, otherwise fallback to generic
    const description = schoolBlurb?.shortSummary || 
      `Learn about ${school.Name} costs, tuition, financial aid, and merit scholarships. Calculate your actual cost with Pocketly's AI-powered college cost calculator.`;
    
    const fullDescription = schoolBlurb?.fullBlurb || 
      `Learn about ${school.Name} costs, tuition, financial aid, and merit scholarships. Calculate your actual cost with Pocketly's AI-powered college cost calculator.`;

    // Update document title
    document.title = `${school.Name} - College Cost & Financial Aid | Pocketly`;

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', `${school.Name} - College Cost & Financial Aid | Pocketly`);

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', description);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    const slug = school.Name?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') || '';
    ogUrl.setAttribute('content', `https://pocketly.ai/school/${ipedsId}/${slug}`);

    // Remove existing structured data script if it exists
    const existingScript = document.getElementById('school-structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    // Add JSON-LD structured data with full blurb
    const script = document.createElement('script');
    script.id = 'school-structured-data';
    script.type = 'application/ld+json';
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": school.Name,
      "description": fullDescription,
      "url": `https://pocketly.ai/school/${ipedsId}/${slug}`,
      "identifier": {
        "@type": "PropertyValue",
        "name": "IPEDS ID",
        "value": ipedsId
      }
    };

    // Add address if available
    if (school.City || school.State) {
      structuredData["address"] = {
        "@type": "PostalAddress",
        "addressLocality": school.City || "",
        "addressRegion": school.State || "",
        "addressCountry": "US"
      };
    }

    // Add tuition information if available
    if (school['Total_price_for_in_state_students_2022_23']) {
      structuredData["hasOfferCatalog"] = {
        "@type": "OfferCatalog",
        "name": "Tuition and Fees",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "In-State Tuition",
              "description": "Annual in-state tuition and fees"
            },
            "price": school['Total_price_for_in_state_students_2022_23']
          }
        ]
      };
      
      if (school['Total_price_for_out_of_state_students_2022_23']) {
        structuredData.hasOfferCatalog.itemListElement.push({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Out-of-State Tuition",
            "description": "Annual out-of-state tuition and fees"
          },
          "price": school['Total_price_for_out_of_state_students_2022_23']
        });
      }
    }

    // Add additional properties if available
    if (school['4 yr Graduation Rate']) {
      structuredData["graduationRate"] = `${school['4 yr Graduation Rate']}%`;
    }

    if (school['Size (all students)']) {
      const studentCount = parseInt(school['Size (all students)']?.replace(/,/g, '') || 0);
      if (studentCount > 0) {
        structuredData["numberOfStudents"] = studentCount;
      }
    }

    if (school['Mean Earnings of Students Working After 10 Years']) {
      structuredData["aggregateRating"] = {
        "@type": "AggregateRating",
        "ratingValue": "4.5",
        "description": `Mean earnings after 10 years: ${school['Mean Earnings of Students Working After 10 Years']}`
      };
    }

    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup function to restore original title when component unmounts
    return () => {
      document.title = 'Pocketly';
      const script = document.getElementById('school-structured-data');
      if (script) {
        script.remove();
      }
    };
  }, [school, ipedsId, schoolBlurb]);

  // Fetch 4 random recommended schools from mid-tier list
  useEffect(() => {
    const fetchRecommendedSchools = async () => {
      if (!ipedsId) return;
      
      setLoadingRecommended(true);
      console.log('Fetching recommended schools for ipedsId:', ipedsId);
      
      try {
        // Filter out current school from the list
        const availableSchools = MID_TIER_SCHOOL_IDS.filter(id => id !== ipedsId);
        console.log('Available schools after filtering:', availableSchools.length);
        
        // Randomly select 4 schools
        const shuffled = [...availableSchools].sort(() => Math.random() - 0.5);
        const selectedIds = shuffled.slice(0, 4);
        console.log('Selected school IDs:', selectedIds);
        
        // Fetch school data from Firestore
        const schoolPromises = selectedIds.map(async (schoolId) => {
          try {
            const schoolDocRef = doc(db, 'collegeData', schoolId);
            const schoolDocSnap = await getDoc(schoolDocRef);
            
            if (schoolDocSnap.exists()) {
              const schoolData = schoolDocSnap.data();
              console.log(`Fetched school: ${schoolData.Name || schoolId}`);
              return {
                ipedsId: schoolId,
                ...schoolData,
              };
            } else {
              console.warn(`School ${schoolId} not found in Firestore`);
            }
            return null;
          } catch (error) {
            console.error(`Error fetching school ${schoolId}:`, error);
            return null;
          }
        });
        
        const schools = (await Promise.all(schoolPromises)).filter(Boolean);
        console.log('Recommended schools fetched:', schools.length, schools.map(s => s.Name));
        setRecommendedSchools(schools);
      } catch (error) {
        console.error('Error fetching recommended schools:', error);
      } finally {
        setLoadingRecommended(false);
      }
    };
    
    fetchRecommendedSchools();
  }, [ipedsId]);
  



/*
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleSelectStart = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey && (e.key === 'c' || e.key === 'u' || e.key === 's')) || // Prevent Ctrl+C, Ctrl+U, Ctrl+S
        (e.ctrlKey && e.shiftKey && e.key === 'I') || // Prevent Ctrl+Shift+I
        (e.metaKey && (e.key === 'c' || e.key === 'u' || e.key === 's')) || // Prevent Cmd+C, Cmd+U, Cmd+S (Mac)
        (e.metaKey && e.shiftKey && e.key === 'I') // Prevent Cmd+Shift+I (DevTools on Mac)
      ) {
        e.preventDefault();
      }
    };
  
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('keydown', handleKeyDown);
  
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); */

  const isCollegeVisible = Array.isArray(visibleColleges) && visibleColleges.includes(Number(ipedsId));

  const handleButtonClick = async (instruction) => {
    const userMessage = { role: 'user', content: instruction };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setLoading(true);

    let botResponse = '';

    try {
      botResponse = await getChatResponse(user.uid, instruction, "", setShowModal);
    } catch (error) {
      botResponse = 'Something went wrong. Please try again.';
    } finally {
      const botMessage = {
        role: 'bot',
        content: botResponse,
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
      setBotMessage(botMessage.content);
      setLoading(false);
    }
  };

  const handleResetMessages = (customMessage = '') => {
    const welcomeMessage = `You are an advisor for ${school?.Name}. ${customMessage}`;
    setMessages([{ role: 'bot', content: welcomeMessage }]);
    setBotMessage(welcomeMessage);
    setUserData({});
    setCurrentStep('welcome');
  };

  const formatMeritData = (data) => {
    // Ensure data exists
    if (!data) {
      console.error('No data provided or data is null/undefined.');
      return null;
    }
  
    // Log type and value of data for debugging
    console.log('Data received:', data);
    console.log('Type of data:', typeof data);
  
    // Handle non-string data
    if (typeof data !== 'string') {
      if (typeof data === 'object' || Array.isArray(data)) {
        // If it's an object or array, convert to a string (JSON.stringify)
        data = JSON.stringify(data);
      } else {
        // If it's something else, convert to a plain string
        data = String(data);
      }
    }
  
    // Split the CSV string into rows
    const rows = data.split('\n').filter((row) => row.trim() !== '');
  
    // Split each row into fields by semicolon
    const formattedData = rows.map((row) => {
      const fields = row.split(';').map((field) => field.trim());
      return {
        scholarshipName: fields[0] || '',
        aidAmount: fields[1] || '',
        criteria: fields[2] || '',
        deadline: fields[3] || '',
        additionalInfo: fields[4] || '',
        link: fields[5] || '',
      };
    });
  
    return formattedData;
  };
  

  const fetchMeritData = async (refresh = false) => {
    console.log('🔄 Starting fetchMeritData with refresh:', refresh); // Log refresh state
    setLoading(true);
  
    try {
      console.log('📄 Attempting to fetch school document for IPEDS ID:', ipedsId); // Log IPEDS ID
      const schoolDocRef = doc(db, 'collegeData', ipedsId);
      const schoolDocSnap = await getDoc(schoolDocRef);
  
      if (schoolDocSnap.exists()) {
        console.log('✅ School document fetched successfully.');
        const schoolDetails = schoolDocSnap.data();
        console.log('📋 School Details:', schoolDetails); // Log school details
  
        if (schoolDetails?.meritDataTable && !refresh) {
          console.log('🟢 Using cached meritDataTable from Firestore.');
          setMeritData(schoolDetails.meritDataTable);
        } else {
          console.log('🔄 Fetching merit data from ChatGPT API...');
          const response = await getChatResponseFree(
            user.uid,
            `Give me info on merit scholarships (non need) I can apply for at ${school.Name} in a CSV string but instead of commas to separate values use semicolons. DONT SAY "Sure, here is the requested information in CSV format!" Just give string to be parsed with headers. The headers should be scholarship name, aid amount, criteria, deadline, additional info, and link to scholarship.`,
            "",
            setShowModal
          );
  
          console.log('✅ Merit data fetched from API:', response); // Log API response
          setMeritData(formatMeritData(response));
  
          console.log('🔄 Updating Firestore with new merit data...');
          await updateDoc(schoolDocRef, {
            meritDataTable: response,
          });
          console.log('✅ Firestore updated with new merit data.');
        }
      } else {
        console.warn('⚠️ No school document found for IPEDS ID:', ipedsId); // Warn if document doesn't exist
      }
    } catch (err) {
      console.error('🚨 Error in fetchMeritData:', err); // Log any errors
    } finally {
      setLoading(false);
      console.log('🔚 Finished fetchMeritData.'); // Indicate completion
    }
  };
  

  const fetchScholarshipData = async (refresh = false) => {
    setLoading(true);
    try {
      const schoolDocRef = doc(db, 'collegeData', ipedsId);
      const schoolDocSnap = await getDoc(schoolDocRef);

      if (schoolDocSnap.exists()) {
        const schoolDetails = schoolDocSnap.data();

        if (schoolDetails?.scholarshipDataTable && !refresh) {
          setScholarshipData(schoolDetails.scholarshipDataTable);
        } else {
          const response = await getChatResponseFree(user.uid, `Give me info on other scholarships I can apply for at ${school.Name} in a CSV string but instead of commas to separate values use semicolons. DONT SAY "Sure, here is the requested information in CSV format!" Just give string to be parsed with headers. The headers should be scholarship name, aid amount, criteria, deadline, separate application required, need-based, additional info, and link to scholarship.`, "", setShowModal);
          setScholarshipData(response);
          await updateDoc(schoolDocRef, {
            scholarshipDataTable: response,
          });
        }
      }
    } catch (err) {
      //setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWithPrompt = async (prompt) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setLoading2(true);
    setAdvisorError(null);
    try {
      const fullPrompt = `${prompt} ${input}`;
      const response = await getChatResponse(user.uid, fullPrompt, "", setShowModal);
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'user', content: input },
        { role: 'bot', content: response },
      ]);
    } catch (err) {
      setAdvisorError('Failed to fetch advisor response. Please try again.');
    } finally {
      setLoading2(false);
    }
  };

  if (!school) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {showModal && <Modal message="API call limit exceeded. Please upgrade your plan." onClose={() => setShowModal(false)} />}
      {showLoginModal && <LoginModal message="Login to access this feature." onClose={() => setShowLoginModal(false)} />}
      <div className="school-details-container">
        <div className="school-details-content">
          <Card className="school-details h-[200px]">
            <CardHeader>
              <CardTitle><h1 className="text-2xl ">
    {school.Name}
    
  </h1></CardTitle>
            </CardHeader>
            
            <CardContent>
            <div className="custom-flex-wrap">
            <span className="badge badge-pill badge-college">
                In-state: {school['Total_price_for_in_state_students_2022_23']}
              </span>
              <span className="badge badge-pill badge-college">
                Out-of-state: {school['Total_price_for_out_of_state_students_2022_23']}
              </span>
                <span className="badge badge-pill badge-college">
                  Percent receiving merit award: {isCollegeVisible ? (
                    `${school['Percent_Freshman_without_need_receiving_merit_aid'] || '15'}`
                  ) : (
                    <UpgradeTooltip>
                      <span className="blurred-text">
                        {`${school['Percent_Freshman_without_need_receiving_merit_aid'] || '15'}%`}
                      </span>
                    </UpgradeTooltip>
                  )}
                </span>
                <span className="badge badge-pill badge-college">
                  Avg merit award: {isCollegeVisible ? (
                    school['Avg_merit_award_for_Freshman_without_need'] || '$7,500'
                  ) : (
                    <UpgradeTooltip>
                      <span className="blurred-text">
                        {school['Avg_merit_award_for_Freshman_without_need'] || '$7,500'}
                      </span>
                    </UpgradeTooltip>
                  )}
                </span>
                <span className="badge badge-pill badge-college">
                  4 yr Graduation Rate: {school['4 yr Graduation Rate']}%
                </span>
                <span className="badge badge-pill badge-college">
                  Total Students: {school['Size (all students)']}
                </span>
                <span className="badge badge-pill badge-college">
                  Mean Earnings After 10 Years: {school['Mean Earnings of Students Working After 10 Years']}
                </span>
              </div>
            </CardContent>
          </Card>


      <div className="chat-container-wrapper">
          <Card className="chat-container">
            
            {/*
              <CardHeader>
                <CardTitle>School Scholarships</CardTitle>
              </CardHeader>
              */}
            <CardContent className="chat-box">
              
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                setActiveTab(value);
            if (value === 'fetchScholarshipData') fetchScholarshipData(false);
            else if (value === 'refreshScholarshipData') fetchScholarshipData(true);
          }}>
            <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="fetchMeritData" className="flex items-center justify-between w-full">
            <div className="relative w-full flex items-center justify-center">
  {/* Centered Title */}
  <span className="text-center w-full">Merit Aid Scholarships</span>
</div>

</TabsTrigger>

              {/*<TabsTrigger value="refreshMeritData">Refresh Merit Aid Information</TabsTrigger>*/}


              {isCollegeVisible ? (
    <TabsTrigger value="fetchScholarshipData">Other Scholarships</TabsTrigger>
  ) : (
    <UpgradeTooltipNoBlur>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', cursor: 'not-allowed' }}>
        <TabsTrigger value="fetchScholarshipData" disabled>
          Other Scholarships
        </TabsTrigger>
        <div className="lock" style={{ position: 'absolute', right: '10px' }}>
          <FaLock style={{ fontSize: '.85em' }} />
        </div>
        
      </div>
      
    </UpgradeTooltipNoBlur>
    
  )}

              {/*<TabsTrigger value="refreshScholarshipData">Refresh Scholarship Information</TabsTrigger>*/}
            </TabsList>




        

            

            <TabsContent value="fetchMeritData">
  {loading ? (
    <div>Loading...</div>
  ) : error ? (
    <div>{error}</div>
  ) : !meritData ? (
    <div>Loading data...</div>
  ) : (
    <ScholarshipTable data={meritData} ipedsId={ipedsId} />
    
  )}

</TabsContent>



           
          <TabsContent value="fetchScholarshipData">
            {loading && <div>Loading...</div>}
            {error && <div>{error}</div>}
            {scholarshipData && <ScholarshipTable data={scholarshipData} ipedsId={ipedsId}/>}
            
          </TabsContent>

         
            <TabsContent value="refreshScholarshipData">
              <Card>
                <CardHeader>
                  <CardTitle>Refresh Scholarship Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {/* Add any specific content or inputs for this tab here */}
                  <Button onClick={() => fetchScholarshipData(true)}>Refresh</Button>
                </CardContent>
              </Card>
            </TabsContent>
            
          </Tabs>
             
              
            </CardContent>
            <p className="text-xs text-gray-600 mt-2 text-center">
  Disclaimer: Pocketly can make mistakes. Its best to verify the information shown here.
</p>
          </Card>
        </div>

      {/* Recommended Schools Section - Right below scholarship table on desktop, hidden on mobile */}
      <div className="hidden md:block mt-0 px-6 pt-4 pb-6" style={{ marginTop: 0 }}>
        {loadingRecommended ? (
          <p className="text-xs text-gray-500">Loading...</p>
        ) : recommendedSchools.length > 0 ? (
          <div>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Recommended Similar Schools</h3>
            <div className="flex flex-wrap gap-2">
              {recommendedSchools.map((recommendedSchool) => {
                const schoolSlug = recommendedSchool.Name?.toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^\w-]/g, '') || '';
                
                return (
                  <Link
                    key={recommendedSchool.ipedsId}
                    to={`/school/${recommendedSchool.ipedsId}/${schoolSlug}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors border border-gray-300 text-xs no-underline"
                  >
                    <span className="font-normal text-gray-800">
                      {recommendedSchool.Name}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      </div>
  
      <div className="screen-container">
        <Card className="school-chart w-[400px]">
          {/*
          <CardHeader>
            <CardTitle>Financial Breakdown</CardTitle>
          </CardHeader>
          */}
          <CardContent className="p-0 no-outline">
            <PieChartComponent uid={uid} ipedsId={ipedsId} myColleges={myColleges} visibleColleges={visibleColleges} className="p-0"/>
          </CardContent>
        </Card>
        
        <div className="chat-container2-wrapper">
          <Card className="chat-container2">
            <CardHeader>
              <CardTitle>{school.Name} AI Advisor</CardTitle>
            </CardHeader>
            <CardContent className="chat-box">
              <div className="messages">
                {loading2 && <div>Loading...</div>}
                {advisorError && <div className="text-red-500 text-sm mb-2">{advisorError}</div>}
                {messages.map((msg, index) => (
                  <div key={index} className={`message ${msg.role}`}>
                    <Typewriter
                      words={[msg.content]}
                      loop={1}
                      typeSpeed={20}
                      deleteSpeed={50}
                      delaySpeed={1000}
                    />
                  </div>
                ))}
              </div>
              <div className="css-6n9yju">
                <input
                  placeholder="Type your message here..."
                  className="chakra-input css-1pgcnou"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  mr={2}
                />
                
                <Button type="button" onClick={() => handleSubmitWithPrompt(`Be confident and concise in questions asked, you are an advisor for ${school.Name}`)}>Submit</Button>
                {/* <Button type="button" onClick={() => handleResetMessages(setMessages)}>Reset Messages</Button>*/}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recommended Schools Section - At bottom on mobile */}
      <div className="block md:hidden mt-4 px-6 pt-4 pb-6">
        {loadingRecommended ? (
          <p className="text-xs text-gray-500">Loading...</p>
        ) : recommendedSchools.length > 0 ? (
          <div>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Recommended Similar Schools</h3>
            <div className="flex flex-wrap gap-2">
              {recommendedSchools.map((recommendedSchool) => {
                const schoolSlug = recommendedSchool.Name?.toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^\w-]/g, '') || '';
                
                return (
                  <Link
                    key={recommendedSchool.ipedsId}
                    to={`/school/${recommendedSchool.ipedsId}/${schoolSlug}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors border border-gray-300 text-xs no-underline"
                  >
                    <span className="font-normal text-gray-800">
                      {recommendedSchool.Name}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
    </>
  );
  
};

export default SchoolDetails;
